import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, PageHeader, Section } from '../../shared/ui/page';

/**
 * The Release 1 financial reports (CLR-13; FRD-FIN-053, FIN-093, FIN-129 to FIN-134, FIN-038),
 * each offered to a session holding the permission that reads its figures.
 */
export const FINANCIAL_REPORTS: readonly {
  readonly to: string;
  readonly title: string;
  readonly detail: string;
  readonly capability: string;
}[] = [
  {
    to: '/reports/trial-balance',
    title: 'Trial balance',
    detail: 'Opening, period and closing balance of every account, or rolled up to a group.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/profit-and-loss',
    title: 'Profit & loss',
    detail: 'Income and expenses from the Chart of Accounts hierarchy.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/balance-sheet',
    title: 'Balance sheet',
    detail: 'The position as at a date.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/account-ledger',
    title: 'General ledger / account ledger',
    detail: 'The posted transactions behind an account balance.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/expenses',
    title: 'Expense report',
    detail: 'Expenses by category, cost centre, employee, period, CAPEX/OPEX or Direct/Indirect.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/revenue',
    title: 'Revenue report',
    detail: 'Revenue by revenue account, client and project.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/customer-outstanding',
    title: 'Customer outstanding / receivables',
    detail: 'What each client owes, what is overdue, and advances held.',
    capability: 'finance.customer_invoice:view',
  },
  {
    to: '/reports/supplier-outstanding',
    title: 'Supplier outstanding / payables',
    detail: 'What is owed to each supplier, what is overdue, and advances paid.',
    capability: 'finance.supplier_invoice:view',
  },
  {
    to: '/bank-cash',
    title: 'Cash & bank position',
    detail: 'Every register with its balance from posted movements.',
    capability: 'finance.bank_cash_account:view',
  },
  {
    to: '/reports/vat-transactions',
    title: 'VAT transaction listing',
    detail: 'Every VAT detail by code and treatment, with its source document.',
    capability: 'tax.vat_transaction_detail:view',
  },
  {
    to: '/reports/vat-exceptions',
    title: 'VAT exceptions',
    detail: 'Missing, overridden, unresolved or inconsistent VAT data.',
    capability: 'tax.vat_transaction_detail:view',
  },
  {
    to: '/reports/vat-reconciliation',
    title: 'VAT control reconciliation',
    detail: 'VAT accounts against the VAT details, and recorded period reconciliations.',
    capability: 'tax.vat_transaction_detail:view',
  },
  {
    to: '/reports/taxable-supplies',
    title: 'Taxable supplies against the threshold',
    detail: 'Cumulative taxable supplies compared with the configured registration threshold.',
    capability: 'tax.vat_transaction_detail:view',
  },
  {
    to: '/reports/reconciliation-status',
    title: 'Reconciliation status',
    detail: 'Accounts marked Reconciliation Required, with their balance and open items.',
    capability: 'ledger.journal:view',
  },
  {
    to: '/reports/posting-rejections',
    title: 'Posting validation report',
    detail: 'Refused postings and the rules that refused them.',
    capability: 'ledger.journal:view',
  },
];

export function FinancialReportsPage() {
  const session = useSession();
  const reports = FINANCIAL_REPORTS.filter((r) => hasCapability(session.data, r.capability));
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Financial reports"
        description="Figures come from posted transactions at the moment each report is opened."
      />
      {reports.length === 0 ? (
        <Section>
          <EmptyState title="No report is available to you" />
        </Section>
      ) : (
        <Grid container spacing={2}>
          {reports.map((report) => (
            <Grid key={report.to} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={RouterLink} to={report.to} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" component="h2">
                      {report.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.detail}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}
